// Transfer Lab Form Types with ID-based structure

export type TransferLabForm = {
  artworks: Record<string, ArtworkFormData>; // Keyed by artwork ID
  artworkOrder: string[]; // To maintain order
};

export type ArtworkFormData = {
  id: string;
  name: string;
  url?: string;
  transferTypes: Record<string, TransferTypeData>; // Keyed by transfer type ID (ordered)
};

export type TransferTypeData = {
  id: string; // 'standard', 'procolor', 'uv', 'glitter'
  name: string;
  presets: Record<string, PresetData>; // Keyed by preset ID
  presetOrder: string[]; // To maintain order
  customPreset: CustomPresetData;
};

export type PresetData = {
  id: string; // 'full-front', 'left-chest', etc.
  name: string;
  sizes: SizeQuantity[];
};

export type CustomPresetData = {
  id: string; // 'custom' or unique ID
  customSizes: CustomSize[];
};

export type CustomSize = {
  id: string; // Unique ID for each custom size
  width: number;
  height: number;
  quantity: number;
};

export type SizeQuantity = {
  id: string;
  size: string;
  displayValue: string;
  width: number;
  height: number;
  quantity: number;
};

// Preset size configurations (must match Vue constants exactly)
export const PRESET_SIZE_CONFIGS: Record<string, Omit<SizeQuantity, "id" | "quantity">[]> = {
  "full-front": [
    { size: "adult (XS - S)", displayValue: "Adult (XS - S)", width: 10, height: 12 },
    { size: "adult (M - L)", displayValue: "Adult (M - L)", width: 11, height: 14 },
    { size: "adult (XL)", displayValue: "Adult (XL)", width: 12, height: 14 },
    { size: "adult (XXL+)", displayValue: "Adult (XXL+)", width: 13, height: 15 },
    { size: "youth (XS - S)", displayValue: "Youth (XS - S)", width: 7, height: 7 },
    { size: "youth (M - L)", displayValue: "Youth (M - L)", width: 8, height: 8 },
    { size: "youth (XL)", displayValue: "Youth (XL)", width: 9, height: 9 },
    { size: "toddler (5T - 6T)", displayValue: "Toddler (5T - 6T)", width: 6, height: 6 },
    { size: "toddler (2T - 4T)", displayValue: "Toddler (2T - 4T)", width: 5, height: 5 },
    { size: "infant (6M - 24M)", displayValue: "Infant", width: 4, height: 4 },
    { size: "infant (Newborn)", displayValue: "Newborn", width: 3, height: 3 }
  ],
  "full-back": [
    { size: "adult (XS - M)", displayValue: "Adult (XS - M)", width: 10, height: 13 },
    { size: "adult (L+)", displayValue: "Adult (L+)", width: 12, height: 15 },
    { size: "youth (XS - S)", displayValue: "Youth (XS - S)", width: 9, height: 12 },
    { size: "youth (M+)", displayValue: "Youth (M+)", width: 10, height: 13 },
    { size: "toddler", displayValue: "Toddler", width: 8, height: 10 },
    { size: "infant", displayValue: "Infant", width: 6, height: 8 }
  ],
  "left-chest": [
    { size: "adult (XS - M)", displayValue: "Adult (XS - M)", width: 3, height: 3 },
    { size: "adult (L+)", displayValue: "Adult (L+)", width: 3.5, height: 3.5 },
    { size: "youth (XS - M)", displayValue: "Youth (XS - M)", width: 2.75, height: 2.75 },
    { size: "youth (L+)", displayValue: "Youth (L+)", width: 3, height: 3 },
    { size: "toddler", displayValue: "Toddler", width: 2, height: 2 },
    { size: "infant", displayValue: "Infant", width: 1.5, height: 1.5 }
  ],
  sleeve: [
    { size: "adult (XS - S)", displayValue: "Adult (XS - S)", width: 2.5, height: 2.5 },
    { size: "adult (M - XL)", displayValue: "Adult (M - XL)", width: 3, height: 3 },
    { size: "adult (XXL+)", displayValue: "Adult (XXL+)", width: 3.25, height: 3.25 },
    { size: "youth", displayValue: "Youth", width: 2.5, height: 2.5 },
    { size: "toddler", displayValue: "Toddler", width: 2.25, height: 2.25 },
    { size: "infant", displayValue: "Infant", width: 1.5, height: 1.5 }
  ],
  "back-collar": [
    { size: "adult (XS - L)", displayValue: "Adult (XS - L)", width: 3, height: 3 },
    { size: "adult (L+)", displayValue: "Adult (L+)", width: 3.5, height: 3.5 },
    { size: "youth", displayValue: "Youth", width: 2.5, height: 2.5 },
    { size: "toddler", displayValue: "Toddler", width: 2, height: 2 },
    { size: "infant", displayValue: "Infant", width: 1.75, height: 1.75 }
  ]
};

// Helper type for artwork thumbnails
export type ArtworkThumb = {
  id: number | string;
  name: string;
  url?: string;
  quantity?: number;
};
